import { RefreshCw, Send, Smartphone } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import PageHeader from "../../components/PageHeader";
import { useAuth } from "../../context/AuthContext";
import { useLojas } from "../../hooks/useLojas";
import { getErrorMessage } from "../../utils/errorHandler";
import {
  WhatsappStatusResponse,
  whatsappService,
} from "../../services/whatsappService";

const DEFAULT_MESSAGE = "Teste de integração WhatsApp Dom Belloni";

const Whatsapp = () => {
  const { user } = useAuth();
  const { data: lojas = [], isLoading: isLoadingLojas } = useLojas();

  const [lojaId, setLojaId] = useState<number | null>(null);
  const [statusData, setStatusData] = useState<WhatsappStatusResponse | null>(
    null
  );
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [numero, setNumero] = useState("+5518997169693");
  const [mensagem, setMensagem] = useState(DEFAULT_MESSAGE);

  useEffect(() => {
    if (lojas.length === 0) return;
    setLojaId((current) => {
      if (current != null && lojas.some((l) => l.id === current)) {
        return current;
      }
      const fromUser = user?.lojaId;
      if (fromUser != null && lojas.some((l) => l.id === fromUser)) {
        return fromUser;
      }
      return lojas[0]?.id ?? null;
    });
  }, [lojas, user?.lojaId]);

  const statusLabel = useMemo(() => {
    if (!statusData) return "Desconhecido";

    const labels: Record<WhatsappStatusResponse["status"], string> = {
      disconnected: "Desconectado",
      connecting: "Conectando",
      qr_pending: "Aguardando leitura do QR Code",
      connected: "Conectado",
      reconnecting: "Reconectando",
    };

    return labels[statusData.status];
  }, [statusData]);

  const statusBadgeClass = useMemo(() => {
    if (!statusData) return "bg-neutral-100 text-neutral-700";

    if (statusData.status === "connected") {
      return "bg-success/15 text-success";
    }

    if (
      statusData.status === "qr_pending" ||
      statusData.status === "connecting"
    ) {
      return "bg-warning/15 text-warning";
    }

    if (statusData.status === "reconnecting") {
      return "bg-info/15 text-info";
    }

    return "bg-error/15 text-error";
  }, [statusData]);

  const loadStatus = useCallback(async () => {
    if (lojaId == null) return;
    setIsLoadingStatus(true);
    try {
      const data = await whatsappService.getStatus(lojaId);
      setStatusData(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoadingStatus(false);
    }
  }, [lojaId]);

  useEffect(() => {
    if (lojaId == null) return;
    loadStatus();

    const interval = setInterval(() => {
      whatsappService
        .getStatus(lojaId)
        .then((data) => setStatusData(data))
        .catch(() => undefined);
    }, 3000);

    return () => clearInterval(interval);
  }, [lojaId, loadStatus]);

  const handleConnect = async () => {
    if (lojaId == null) return;
    setIsConnecting(true);
    try {
      const response = await whatsappService.connect(lojaId);
      toast.success(response.message);
      await loadStatus();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (lojaId == null) return;
    setIsDisconnecting(true);
    try {
      const response = await whatsappService.disconnect(lojaId);
      toast.success(response.message);
      await loadStatus();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleSendTest = async () => {
    if (lojaId == null) return;
    setIsSendingTest(true);
    try {
      const response = await whatsappService.sendTestMessage({
        lojaId,
        numero,
        mensagem,
      });
      toast.success(response.message);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSendingTest(false);
    }
  };

  const lojaNome =
    lojas.find((l) => l.id === lojaId)?.nome ??
    (lojaId != null ? `Loja #${lojaId}` : "");

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 mt-6">
        <PageHeader
          title="WhatsApp"
          description="Cada loja tem uma sessão própria. Escolha a loja, gere o QR no celular daquela unidade e teste o envio."
        />
      </div>

      <div className="mb-6 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
        <label
          htmlFor="whatsapp-loja"
          className="mb-2 block text-sm font-medium text-neutral-700"
        >
          Loja
        </label>
        <select
          id="whatsapp-loja"
          value={lojaId ?? ""}
          onChange={(e) => setLojaId(Number(e.target.value))}
          disabled={isLoadingLojas || lojas.length === 0}
          className="w-full max-w-md rounded-lg border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15"
        >
          {lojas.map((l) => (
            <option key={l.id} value={l.id}>
              {l.nome}
            </option>
          ))}
        </select>
        {lojaId != null && (
          <p className="mt-2 text-sm text-neutral-600">
            Configurando sessão para: <strong>{lojaNome}</strong>. As mensagens
            automáticas de retirada usam a sessão da loja do pedido.
          </p>
        )}
      </div>

      <hr className="my-6" />

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">
                Status da conexão
              </h2>
              <p className="mt-1 text-sm text-neutral-600">
                Gere o QR Code e faça a autenticação pelo WhatsApp no celular
                desta loja.
              </p>
            </div>

            <button
              type="button"
              onClick={loadStatus}
              className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
              disabled={isLoadingStatus || lojaId == null}
            >
              <RefreshCw size={16} />
              Atualizar
            </button>
          </div>

          <div className="mb-5 flex items-center gap-3">
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass}`}
            >
              {statusLabel}
            </span>
            {statusData?.connectedNumber && (
              <span className="text-sm text-neutral-700">
                Número conectado: <strong>{statusData.connectedNumber}</strong>
              </span>
            )}
          </div>

          {statusData?.lastError && (
            <p className="mb-4 rounded-lg bg-error/10 px-3 py-2 text-sm text-error">
              {statusData.lastError}
            </p>
          )}

          <div className="mb-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleConnect}
              disabled={isConnecting || lojaId == null}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-70"
            >
              <Smartphone size={16} />
              {isConnecting ? "Gerando QR Code..." : "Gerar QR Code"}
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={isDisconnecting || lojaId == null}
              className="rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isDisconnecting ? "Desconectando..." : "Desconectar sessão"}
            </button>
          </div>

          <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-5">
            {statusData?.qrCode ? (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={statusData.qrCode}
                  alt="QR Code WhatsApp"
                  className="h-64 w-64 rounded-lg border border-neutral-200 bg-white p-2"
                />
                <p className="text-center text-sm text-neutral-600">
                  Abra o WhatsApp no celular, vá em Dispositivos conectados e
                  escaneie o QR Code.
                </p>
              </div>
            ) : (
              <p className="text-sm text-neutral-600">
                QR Code não disponível no momento. Clique em &quot;Gerar QR
                Code&quot;.
              </p>
            )}
          </div>
        </section>

        <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-neutral-900">
            Enviar mensagem de teste
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            Usa a sessão WhatsApp da loja selecionada acima.
          </p>

          <div className="mt-5 space-y-4">
            <div>
              <label
                htmlFor="whatsapp-numero"
                className="mb-1.5 block text-sm font-medium text-neutral-700"
              >
                Número
              </label>
              <input
                id="whatsapp-numero"
                type="text"
                value={numero}
                onChange={(event) => setNumero(event.target.value)}
                placeholder="+5518997169693"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15"
              />
            </div>

            <div>
              <label
                htmlFor="whatsapp-mensagem"
                className="mb-1.5 block text-sm font-medium text-neutral-700"
              >
                Mensagem
              </label>
              <textarea
                id="whatsapp-mensagem"
                value={mensagem}
                onChange={(event) => setMensagem(event.target.value)}
                rows={4}
                className="w-full resize-none rounded-lg border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15"
              />
            </div>

            <button
              type="button"
              onClick={handleSendTest}
              disabled={isSendingTest || lojaId == null}
              className="inline-flex items-center gap-2 rounded-lg bg-success px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <Send size={16} />
              {isSendingTest ? "Enviando..." : "Disparar mensagem teste"}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Whatsapp;
